import { useEffect, useMemo, useState } from "react";
import { CreditModal, SectionRule } from "./components/common";
import { IndicatorSection } from "./components/IndicatorSection";
import { MetricCard } from "./components/MetricCard";
import { DashboardSidebar, SidebarToggleButton } from "./components/Sidebar";
import { SignalsPanel } from "./components/SignalsPanel";
import type { DashboardPayload, Indicator } from "./types";
import { formatDataUrl } from "./utils";

const DATA_URL = formatDataUrl("/data/dashboard.json");

function App() {
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetch(DATA_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return response.json() as Promise<DashboardPayload>;
      })
      .then((payload) => {
        if (active) {
          setDashboard(payload);
        }
      })
      .catch(() => {
        if (active) {
          setError("ダッシュボードデータを読み込めませんでした。");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <main className="app-shell app-shell--centered" aria-hidden={false}>
        <section className="empty-state" role="alert">
          <p>{error}</p>
          <a href={DATA_URL}>生成済みデータを確認</a>
        </section>
      </main>
    );
  }

  if (!dashboard) {
    return (
      <main className="app-shell app-shell--centered" aria-busy="true">
        <section className="loading-panel">
          <span className="loading-bar" />
          <p>データを読み込んでいます。</p>
        </section>
      </main>
    );
  }

  return <DashboardView dashboard={dashboard} />;
}

function useClock(): string {
  const [clock, setClock] = useState(() => formatClock(new Date()));

  useEffect(() => {
    const timer = window.setInterval(() => setClock(formatClock(new Date())), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return clock;
}

function formatClock(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function hashString(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

/**
 * Triggers a browser download for every indicator's CSV export in sequence. Each
 * per-indicator CSV button already downloads a single file (unchanged); this just
 * fans that same mechanism out across all indicators for the header's "全CSV" action.
 */
function downloadAllCsv(indicators: Indicator[]): void {
  for (const indicator of indicators) {
    const link = document.createElement("a");
    link.href = formatDataUrl(indicator.csvPath);
    link.download = "";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

function DashboardView({ dashboard }: { dashboard: DashboardPayload }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    return !window.matchMedia("(max-width: 860px)").matches;
  });
  const [isCreditOpen, setIsCreditOpen] = useState(false);
  const sourceCount = useMemo(
    () => new Set(dashboard.indicators.map((indicator) => indicator.metadata.sourceName)).size,
    [dashboard.indicators]
  );
  const latestPeriod = dashboard.dataAsOf;
  const clock = useClock();
  const buildFingerprint = useMemo(
    () =>
      hashString(`${dashboard.schemaVersion}|${dashboard.generatedAt}|${dashboard.dataAsOf}|${dashboard.indicators.length}`),
    [dashboard.schemaVersion, dashboard.generatedAt, dashboard.dataAsOf, dashboard.indicators.length]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isCreditOpen) {
          setIsCreditOpen(false);
        } else {
          setIsSidebarOpen(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCreditOpen]);

  useEffect(() => {
    if (!isCreditOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCreditOpen]);

  return (
    <>
      <a className="skip-link" href="#indicators">
        指標一覧へ移動
      </a>
      <div className={`dashboard-frame ${isSidebarOpen ? "is-sidebar-open" : "is-sidebar-hidden"}`}>
        <DashboardSidebar
          dashboard={dashboard}
          sourceCount={sourceCount}
          latestPeriod={latestPeriod}
          buildFingerprint={buildFingerprint}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onCreditOpen={() => setIsCreditOpen(true)}
        />
        <button
          className="sidebar-scrim"
          type="button"
          aria-label="サイドバーを閉じる"
          tabIndex={isSidebarOpen ? 0 : -1}
          onClick={() => setIsSidebarOpen(false)}
        />
        <main className="app-shell scrollmain">
          <div className="workspace-topline">
            <SidebarToggleButton
              isOpen={isSidebarOpen}
              onClick={() => setIsSidebarOpen((current) => !current)}
            />
          </div>

          <header className="dashboard-header">
            <div className="dashboard-header__copy">
              <p className="eyebrow">Monthly market signals</p>
              <h1>基本指標</h1>
              <p className="dashboard-header__lead">{dashboard.description}</p>
            </div>
            <div className="dashboard-header__meta">
              <div className="sync-indicator">
                <span className="sync-indicator__dot" aria-hidden="true" />
                <span>SYNC {clock}</span>
              </div>
              <div className="build-meta">
                REV {dashboard.schemaVersion}&nbsp;·&nbsp;GEN {dashboard.generatedAt}&nbsp;·&nbsp;AS OF {dashboard.dataAsOf}
              </div>
              <div className="dashboard-header__actions">
                <button className="ghostbtn" type="button" onClick={() => downloadAllCsv(dashboard.indicators)}>
                  全CSV
                </button>
                <button className="ghostbtn" type="button" onClick={() => setIsCreditOpen(true)}>
                  このサービスについて
                </button>
              </div>
            </div>
          </header>

          <SectionRule index="01" jaLabel="今月のシグナル" enLabel="SIGNALS" note="顕著な変化を捉える" />

          <SignalsPanel signals={dashboard.signals} />

          <SectionRule index="02" jaLabel="代表値" enLabel="KEY METRICS" note="全体の動向を知る" />

          <section className="metric-list" aria-label="最新指標">
            {dashboard.indicators.map((indicator, index) => (
              <MetricCard key={indicator.id} indicator={indicator} index={index} />
            ))}
          </section>

          <SectionRule
            index="03"
            jaLabel="指標別分析"
            enLabel="INDICATOR BREAKDOWN"
            note="共時的 & 通時的分析"
          />

          <section id="indicators" className="indicator-stack" aria-label="指標別詳細">
            {dashboard.indicators.map((indicator, index) => (
              <IndicatorSection key={indicator.id} indicator={indicator} index={index} />
            ))}
          </section>

          <footer className="dashboard-footer">
            <span>本データはe-Statより取得した数値を独自に集計したものです。データの正確性・完全性については保証いたしかねます。</span>
          </footer>
        </main>
        {isCreditOpen ? <CreditModal credit={dashboard.eStatCredit} onClose={() => setIsCreditOpen(false)} /> : null}
      </div>
    </>
  );
}

export default App;
